import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { X, Check, Loader2, AlertTriangle, Users, Calendar, FileText, Clock, BarChart3, CalendarRange, CheckCircle2, Hourglass, XCircle, Ban, ShieldAlert } from 'lucide-react';
import { LeaveRequestView, LeaveTypeView, LeaveRequestStatus, ConflictView, LeaveRequestAttachmentView, TeamOverlapView, EmployeeLeaveContextView, WorkflowInstanceView } from '../../../../models/attendance';
import { getLeaveConflicts, getLeaveRequestAttachments, previewLeaveAttachment, getTeamOverlap, getEmployeeLeaveContext, getWorkflowInstanceByLeaveRequest } from '../../../../api/Attendance/leaves';
import { WorkflowProgressTracker } from '../../../../components/leaves/WorkflowProgressTracker';
import { getLeaveTypeBadgeStyle } from '../../../../utils/leaveTypeUtils';
import { getFullUrl } from '../../../../utils/url';
import { CoverageIndicator } from '../../../../components/leaves/CoverageIndicator';

const STATUS_CHIP_CONFIG: Record<LeaveRequestStatus, { icon: React.ElementType; bg: string; text: string }> = {
  [LeaveRequestStatus.Approved]: { icon: CheckCircle2, bg: 'bg-green-100', text: 'text-green-700' },
  [LeaveRequestStatus.Pending]: { icon: Hourglass, bg: 'bg-amber-100', text: 'text-amber-700' },
  [LeaveRequestStatus.Rejected]: { icon: XCircle, bg: 'bg-red-100', text: 'text-red-700' },
  [LeaveRequestStatus.Cancelled]: { icon: Ban, bg: 'bg-gray-100', text: 'text-gray-600' },
  [LeaveRequestStatus.CancellationPending]: { icon: Clock, bg: 'bg-orange-100', text: 'text-orange-700' },
};

interface RequestDetailModalProps {
  request: LeaveRequestView;
  leaveTypes?: LeaveTypeView[];
  onClose: () => void;
  onApprove?: (requestId: number, comments: string) => Promise<void>;
  onReject?: (requestId: number, comments: string) => Promise<void>;
}

type ActionType = 'approve' | 'reject' | null;

const r = (n: number) => Math.round(n * 100) / 100;

const RequestDetailModal: React.FC<RequestDetailModalProps> = ({ request, leaveTypes, onClose, onApprove, onReject }) => {
  const { t } = useTranslation(['leaves', 'common', 'translation']);
  const currentUserId = useSelector((state: any) => state.user.userId);
  const callbacksProvided = !!(onApprove || onReject);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [comments, setComments] = useState('');
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [actionType, setActionType] = useState<ActionType>(null);
  const [conflicts, setConflicts] = useState<ConflictView[]>([]);
  const [attachments, setAttachments] = useState<LeaveRequestAttachmentView[]>([]);
  const [teamOverlap, setTeamOverlap] = useState<TeamOverlapView | null>(null);
  const [teamOverlapLoading, setTeamOverlapLoading] = useState(false);
  const [employeeContext, setEmployeeContext] = useState<EmployeeLeaveContextView | null>(null);
  const [employeeContextLoading, setEmployeeContextLoading] = useState(false);
  const [workflow, setWorkflow] = useState<WorkflowInstanceView | null>(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [showSuggestDates, setShowSuggestDates] = useState(false);
  const [suggestedStartDate, setSuggestedStartDate] = useState('');
  const [suggestedEndDate, setSuggestedEndDate] = useState('');

  // Determine if user is the designated approver for the current workflow step
  const currentStepApprover = workflow?.steps?.find(
    s => s.stepOrder === workflow.currentStepOrder
  )?.approverId;
  const isCurrentStepApprover = !workflow || !currentStepApprover || currentStepApprover === currentUserId;
  const isReadOnly = !callbacksProvided || (!workflowLoading && !isCurrentStepApprover);

  useEffect(() => {
    loadConflicts();
    loadTeamOverlap();
    loadEmployeeContext();
    loadWorkflow();
    if (request.attachmentCount > 0) {
      loadAttachments();
    }
  }, [request.id]);

  const loadConflicts = async () => {
    try {
      const result = await getLeaveConflicts(request.id);
      if (result.errors.length === 0) {
        setConflicts(result.payload);
      }
    } catch { /* ignore */ }
  };

  const loadTeamOverlap = async () => {
    setTeamOverlapLoading(true);
    try {
      const result = await getTeamOverlap(request.id);
      if (result.errors.length === 0) {
        setTeamOverlap(result.payload);
      }
    } catch { /* ignore */ }
    setTeamOverlapLoading(false);
  };

  const loadEmployeeContext = async () => {
    const leaveType = leaveTypes?.find(lt => lt.name.trim().toLowerCase() === request.leaveType?.trim().toLowerCase());
    if (!leaveType || !request.userId) return;
    setEmployeeContextLoading(true);
    try {
      const result = await getEmployeeLeaveContext(request.userId, leaveType.id);
      if (result.errors.length === 0) {
        setEmployeeContext(result.payload);
      }
    } catch { /* ignore */ }
    setEmployeeContextLoading(false);
  };

  const loadAttachments = async () => {
    try {
      const result = await getLeaveRequestAttachments(request.id);
      if (result.errors.length === 0) {
        setAttachments(result.payload);
      }
    } catch { /* ignore */ }
  };

  const loadWorkflow = async () => {
    setWorkflowLoading(true);
    try {
      const result = await getWorkflowInstanceByLeaveRequest(request.id);
      if (result.errors.length === 0) {
        setWorkflow(result.payload);
      }
    } catch { /* 404 = no workflow for this request */ }
    setWorkflowLoading(false);
  };

  const isCancellationPending = request.status === LeaveRequestStatus.CancellationPending;

  const getConflictSeverityColor = (type: string): string => {
    if (type === 'blackout') return 'border-red-500 bg-red-50 text-red-700';
    if (type === 'team_overlap') return 'border-orange-500 bg-orange-50 text-orange-700';
    return 'border-yellow-500 bg-yellow-50 text-yellow-700';
  };

  const formatDate = (dateString: string | Date): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateRange = (startDate: string | Date, endDate: string | Date): string => {
    const s = new Date(startDate);
    const e = new Date(endDate);
    if (s.toDateString() === e.toDateString()) return formatDate(startDate);
    const start = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${start} - ${end}`;
  };

  const buildComments = (): string => {
    let finalComments = comments;
    if (showSuggestDates && suggestedStartDate && suggestedEndDate) {
      const start = new Date(suggestedStartDate).toLocaleDateString();
      const end = new Date(suggestedEndDate).toLocaleDateString();
      finalComments += `\n\n${t('suggestedAlternativeLine', 'Suggested alternative: {{start}} - {{end}}', { start, end })}`;
    }
    return finalComments;
  };

  const handleAction = async (type: 'approve' | 'reject') => {
    const finalComments = buildComments();
    if (type === 'approve' && onApprove) {
      setIsApproving(true);
      try {
        await onApprove(request.id, finalComments);
        onClose();
      } finally {
        setIsApproving(false);
      }
    } else if (type === 'reject' && onReject) {
      setIsRejecting(true);
      try {
        await onReject(request.id, finalComments);
        onClose();
      } finally {
        setIsRejecting(false);
      }
    }
  };

  const handleCommentSubmit = () => {
    if (actionType) handleAction(actionType);
    setShowCommentForm(false);
    setActionType(null);
  };

  const initiateAction = (type: 'approve' | 'reject') => {
    setActionType(type);
    setShowCommentForm(true);
  };

  const handlePreviewAttachment = async (attachmentId: number) => {
    try {
      const blob = await previewLeaveAttachment(attachmentId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" data-testid="request-detail-modal">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                {getFullUrl(request.user?.imageUrl) ? (
                  <img src={getFullUrl(request.user?.imageUrl)!} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl font-medium text-muted-foreground">
                    {request.user?.fullName?.charAt(0) || '?'}
                  </span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-foreground">{request.user?.fullName}</h2>
                  {(() => {
                    const statusCfg = STATUS_CHIP_CONFIG[request.status] ?? STATUS_CHIP_CONFIG[LeaveRequestStatus.Pending];
                    const StatusIcon = statusCfg.icon;
                    return (
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.text}`}>
                        <StatusIcon className="h-3.5 w-3.5" />
                        {t(`translation:${request.status}`, request.status)}
                      </span>
                    );
                  })()}
                </div>
                <p className="text-muted-foreground">{request.user?.title}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="leave-request-detail-close">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Leave Period - prominent at top */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">{t('translation:leavePeriod', 'Leave Period')}</h3>
            <div className="bg-muted/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-foreground mb-2">
                {formatDateRange(request.startDate, request.endDate)}
              </p>
              <p className="text-muted-foreground">
                {new Date(request.startDate).toDateString() === new Date(request.endDate).toDateString()
                  ? formatDate(request.startDate)
                  : `${t('common:From Date')} ${formatDate(request.startDate)} ${t('translation:to', 'to')} ${formatDate(request.endDate)}`
                }
              </p>
            </div>
          </div>

          {/* Cancellation Banner */}
          {isCancellationPending && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="cancellation-banner">
              <div className="flex items-start space-x-3 rtl:space-x-reverse">
                <AlertTriangle size={20} className="text-orange-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-orange-800">
                    {t('cancellationRequested', 'Cancellation Requested')}
                  </p>
                  <p className="text-sm text-orange-700 mt-1">
                    {t('cancellationExplanation', 'The employee has requested to cancel this previously approved leave. Please review and approve or reject the cancellation.')}
                  </p>
                  {request.cancelReason && (
                    <p className="text-sm text-orange-700 mt-2">
                      <span className="font-medium">{t('Reason')}:</span> {request.cancelReason}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Leave Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">{t('Leave Request Details')}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('Leave Type')}:</span>
                  <div
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getLeaveTypeBadgeStyle(request.leaveType, leaveTypes).colorClass}`}
                    style={getLeaveTypeBadgeStyle(request.leaveType, leaveTypes).style}
                  >
                    {t(request.leaveType)}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('translation:duration', 'Duration')}:</span>
                  <span className="font-medium text-foreground">
                    {r(request.numberOfDays)} {request.numberOfDays === 1 ? t('translation:day', 'day') : t('translation:days', 'days')}
                    {request.fromHalfDay && ` (${t('Half day')})`}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('translation:status', 'Status')}:</span>
                  <span className="font-medium text-foreground">{t(request.status)}</span>
                </div>
                {request.dateApplied && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('Date Applied')}:</span>
                    <span className="font-medium text-foreground">{formatDate(request.dateApplied)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">{t('translation:employeeInfo', 'Employee Info')}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('translation:Department')}:</span>
                  <span className="font-medium text-foreground">{request.userDepartmentName || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('translation:Branch')}:</span>
                  <span className="font-medium text-foreground">{request.userBranchName || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('translation:manager', 'Manager')}:</span>
                  <span className="font-medium text-foreground">{request.userManagerName || '-'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Workflow Progress */}
          {workflowLoading ? (
            <div className="flex items-center space-x-2 rtl:space-x-reverse text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">{t('workflowLoading', 'Loading workflow progress...')}</span>
            </div>
          ) : workflow && (
            <div data-testid="workflow-progress-section">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2 rtl:space-x-reverse">
                <Clock size={20} />
                <span>{t('approvalWorkflow', 'Approval Workflow')}</span>
                <span className="text-sm font-normal text-muted-foreground">({workflow.workflowName})</span>
              </h3>
              <div className="bg-muted/30 rounded-lg p-4">
                <WorkflowProgressTracker workflow={workflow} variant="detailed" />
              </div>
              {callbacksProvided && !isCurrentStepApprover && (
                <div className="mt-3 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <ShieldAlert size={16} className="shrink-0" />
                  <span>{t('notDesignatedApprover', 'You are not the designated approver for the current workflow step.')}</span>
                </div>
              )}
            </div>
          )}

          {/* Reviewers (shown when no workflow tracker, or as extra detail) */}
          {!workflowLoading && !workflow && request.reviewers && request.reviewers.length > 0 && (
            <div data-testid="reviewers-section">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2 rtl:space-x-reverse">
                <Clock size={20} />
                <span>{t('reviewHistory', 'Review History')}</span>
              </h3>
              <div className="space-y-3">
                {request.reviewers.map((reviewer, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                    <div className={`w-2.5 h-2.5 mt-1.5 rounded-full shrink-0 ${
                      reviewer.status === 'Approved' ? 'bg-green-500' :
                      reviewer.status === 'Rejected' ? 'bg-red-500' : 'bg-orange-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">{reviewer.reviewerName}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            reviewer.status === 'Approved' ? 'bg-green-100 text-green-700' :
                            reviewer.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                            {reviewer.status && t(`translation:${reviewer.status}`, reviewer.status)}
                          </span>
                          {reviewer.actionDate && (
                            <span className="text-xs text-muted-foreground">
                              {new Date(reviewer.actionDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      {reviewer.comments && (
                        <p className="text-sm text-muted-foreground mt-1">{reviewer.comments}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Employee Context */}
          {employeeContextLoading ? (
            <div className="flex items-center space-x-2 rtl:space-x-reverse text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">{t('employeeContextLoading', 'Loading employee context...')}</span>
            </div>
          ) : employeeContext && (
            <div data-testid="employee-context-panel">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2 rtl:space-x-reverse">
                <BarChart3 size={20} />
                <span>{t('employeeContext', 'Employee Context')}</span>
              </h3>
              <div className="space-y-4">
                {/* Balance progress bar */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">{t('employeeContextBalance', 'Balance')}</span>
                    <span className="text-sm font-medium text-foreground">
                      {r(employeeContext.remainingBalance)} / {r(employeeContext.totalEntitlement)} {t('translation:days', 'days')}
                    </span>
                  </div>
                  <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        employeeContext.totalEntitlement > 0 && (employeeContext.remainingBalance / employeeContext.totalEntitlement) < 0.2
                          ? 'bg-red-500' : employeeContext.totalEntitlement > 0 && (employeeContext.remainingBalance / employeeContext.totalEntitlement) < 0.4
                          ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${employeeContext.totalEntitlement > 0 ? Math.min(100, (employeeContext.remainingBalance / employeeContext.totalEntitlement) * 100) : 0}%` }}
                    />
                  </div>
                </div>

                {/* Usage stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-foreground">{r(employeeContext.daysTakenThisYear)}</p>
                    <p className="text-xs text-muted-foreground">{t('employeeContextThisYear', 'This Year')}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-foreground">{r(employeeContext.daysTakenThisMonth)}</p>
                    <p className="text-xs text-muted-foreground">{t('employeeContextThisMonth', 'This Month')}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-foreground">{employeeContext.requestsThisQuarter}</p>
                    <p className="text-xs text-muted-foreground">{t('employeeContextThisQuarter', 'This Quarter')}</p>
                  </div>
                </div>

                {/* Recent leaves */}
                {employeeContext.recentLeaves.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">{t('employeeContextRecent', 'Recent Leaves')}</p>
                    <div className="space-y-1.5">
                      {employeeContext.recentLeaves.map((leave, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-sm py-1.5 px-3 bg-muted/20 rounded"
                          data-testid="employee-context-recent-leave"
                        >
                          <div className="flex items-center space-x-2 rtl:space-x-reverse min-w-0">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              leave.status === 'Approved' ? 'bg-green-500' : 'bg-yellow-500'
                            }`} />
                            <span className="text-foreground truncate">{formatDateRange(leave.startDate, leave.endDate)}</span>
                          </div>
                          <div className="flex items-center space-x-2 rtl:space-x-reverse shrink-0">
                            <span className="text-muted-foreground">{t(leave.leaveType)}</span>
                            <span className="text-foreground font-medium">({r(leave.days)}d)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Team Availability */}
          {teamOverlapLoading ? (
            <div className="flex items-center space-x-2 rtl:space-x-reverse text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">{t('teamOverlapLoading', 'Loading team availability...')}</span>
            </div>
          ) : teamOverlap && teamOverlap.totalTeamMembers > 0 && (
            <div data-testid="team-overlap-panel">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2 rtl:space-x-reverse">
                <Users size={20} />
                <span>{t('teamAvailabilityDuringPeriod', 'Team Availability During This Period')}</span>
              </h3>
              <div className="space-y-4">
                {/* Coverage bar */}
                <CoverageIndicator
                  totalMembers={teamOverlap.totalTeamMembers}
                  offCount={teamOverlap.ifApproved}
                  variant="bar"
                />

                {/* Overlapping members list */}
                {teamOverlap.members.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">
                      {t('teamOverlapCurrentlyOff', 'Currently off during this period:')}
                    </p>
                    <div className="space-y-2">
                      {teamOverlap.members.map((member, idx) => (
                        <div
                          key={`${member.userId}-${idx}`}
                          className="flex items-center space-x-3 rtl:space-x-reverse p-3 bg-muted/30 rounded-lg"
                          data-testid="team-overlap-member"
                        >
                          {/* Status indicator */}
                          <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                            member.status === 'Approved' ? 'bg-green-500' : 'bg-yellow-500'
                          }`} />
                          {/* Avatar */}
                          <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex items-center justify-center shrink-0">
                            {getFullUrl(member.imageUrl) ? (
                              <img src={getFullUrl(member.imageUrl)!} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs font-medium text-muted-foreground">
                                {member.userName?.charAt(0) || '?'}
                              </span>
                            )}
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{member.userName}</p>
                            <p className="text-xs text-muted-foreground">{t(member.leaveType)}</p>
                          </div>
                          {/* Date range */}
                          <div className="flex items-center space-x-1 rtl:space-x-reverse text-xs text-muted-foreground shrink-0">
                            <Clock size={12} />
                            <span>{formatDateRange(member.startDate, member.endDate)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Legend */}
                    <div className="flex items-center space-x-4 rtl:space-x-reverse text-xs text-muted-foreground pt-1">
                      <div className="flex items-center space-x-1.5 rtl:space-x-reverse">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        <span>{t('translation:Approved', 'Approved')}</span>
                      </div>
                      <div className="flex items-center space-x-1.5 rtl:space-x-reverse">
                        <div className="w-2 h-2 rounded-full bg-yellow-500" />
                        <span>{t('translation:Pending', 'Pending')}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* No overlaps message */}
                {teamOverlap.members.length === 0 && (
                  <p className="text-sm text-green-600">
                    {t('teamOverlapNoConflicts', 'No team members are off during this period.')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Reason */}
          {request.comments && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">{t('Reason')}</h3>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-foreground">{request.comments}</p>
              </div>
            </div>
          )}

          {/* Conflicts */}
          {conflicts.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">{t('translation:conflicts', 'Potential Conflicts')}</h3>
              <div className="space-y-3">
                {conflicts.map((conflict, index) => (
                  <div key={index} className={`p-4 rounded-lg border-s-4 ${getConflictSeverityColor(conflict.type)}`}>
                    <div className="flex items-start space-x-3 rtl:space-x-reverse">
                      {conflict.type === 'team_overlap' ? (
                        <Users size={20} className="mt-0.5" />
                      ) : conflict.type === 'blackout' ? (
                        <AlertTriangle size={20} className="mt-0.5" />
                      ) : (
                        <Calendar size={20} className="mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium capitalize">{t(conflict.type?.replace('_', ' ') ?? '')}</p>
                        <p className="text-sm">{conflict.description}</p>
                        {conflict.userName && (
                          <p className="text-xs mt-1">{conflict.userName}</p>
                        )}
                      </div>
                      {conflict.coverageImpactPercent != null && (
                        <div className="px-2 py-1 rounded text-xs font-medium bg-red-200 text-red-800">
                          -{conflict.coverageImpactPercent}%
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">{t('Attachments')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center space-x-3 rtl:space-x-reverse p-3 bg-muted/30 rounded-lg">
                    <FileText size={20} className="text-muted-foreground" />
                    <span className="flex-1 text-foreground text-sm truncate">{attachment.name}</span>
                    <button
                      onClick={() => handlePreviewAttachment(attachment.id)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      data-testid="leave-request-detail-view-attachment"
                    >
                      {t('View')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comment Form */}
          {!isReadOnly && showCommentForm && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {t('Manager Comments')}
                {actionType === 'reject' && (
                  <span className="text-red-600 text-sm ltr:ml-2 rtl:mr-2">({t('translation:required', 'Required')})</span>
                )}
              </h3>
              <div className="space-y-4">
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder={actionType === 'approve'
                    ? t('translation:approvalCommentsPlaceholder', 'Add any approval comments (optional)...')
                    : t('translation:rejectionCommentsPlaceholder', 'Please provide a reason for rejection...')
                  }
                  data-testid="modal-comments"
                  className="w-full h-32 px-4 py-3 bg-card border border-border rounded-lg text-foreground placeholder-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                />
                {actionType === 'reject' && !isCancellationPending && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setShowSuggestDates(!showSuggestDates)}
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                      data-testid="modal-suggest-dates-toggle"
                    >
                      <CalendarRange size={14} />
                      {t('suggestAlternativeDates', 'Suggest alternative dates')}
                    </button>
                    {showSuggestDates && (
                      <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex-1 space-y-1">
                          <label className="text-xs text-blue-700 font-medium">
                            {t('common:From Date', 'From')}
                          </label>
                          <input
                            type="date"
                            value={suggestedStartDate}
                            onChange={(e) => setSuggestedStartDate(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm bg-card border border-border rounded text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            data-testid="modal-suggest-dates-start"
                          />
                        </div>
                        <div className="flex-1 space-y-1">
                          <label className="text-xs text-blue-700 font-medium">
                            {t('common:To Date', 'To')}
                          </label>
                          <input
                            type="date"
                            value={suggestedEndDate}
                            min={suggestedStartDate || undefined}
                            onChange={(e) => setSuggestedEndDate(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm bg-card border border-border rounded text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            data-testid="modal-suggest-dates-end"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-end space-x-3 rtl:space-x-reverse">
                  <button
                    onClick={() => { setShowCommentForm(false); setActionType(null); setComments(''); setShowSuggestDates(false); setSuggestedStartDate(''); setSuggestedEndDate(''); }}
                    className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="leave-request-detail-cancel-comment"
                  >
                    {t('translation:cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleCommentSubmit}
                    disabled={(actionType === 'reject' && !comments.trim()) || isApproving || isRejecting}
                    className={`px-6 py-2 text-white rounded-lg transition-colors ${
                      actionType === 'approve'
                        ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-400'
                        : 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                    }`}
                    data-testid="leave-request-detail-submit-comment"
                  >
                    {actionType === 'approve'
                      ? (isCancellationPending ? t('approveCancellation', 'Approve Cancellation') : t('Approve'))
                      : (isCancellationPending ? t('rejectCancellation', 'Reject Cancellation') : t('Reject'))
                    }
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions Footer */}
        {!isReadOnly && !showCommentForm && (
          <div className="p-6 border-t border-border bg-muted/30">
            <div className="flex items-center justify-end space-x-4 rtl:space-x-reverse">
              <button
                onClick={() => initiateAction('reject')}
                disabled={isApproving || isRejecting}
                data-testid="modal-reject-btn"
                className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium rounded-lg transition-colors flex items-center"
              >
                {isRejecting ? (
                  <><Loader2 size={20} className="ltr:mr-2 rtl:ml-2 animate-spin" />{t('translation:rejecting', 'Rejecting...')}</>
                ) : (
                  <><X size={20} className="ltr:mr-2 rtl:ml-2" />{isCancellationPending ? t('rejectCancellation', 'Reject Cancellation') : t('Reject')}</>
                )}
              </button>
              <button
                onClick={() => initiateAction('approve')}
                disabled={isApproving || isRejecting}
                data-testid="modal-approve-btn"
                className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg transition-colors flex items-center"
              >
                {isApproving ? (
                  <><Loader2 size={20} className="ltr:mr-2 rtl:ml-2 animate-spin" />{t('translation:approving', 'Approving...')}</>
                ) : (
                  <><Check size={20} className="ltr:mr-2 rtl:ml-2" />{isCancellationPending ? t('approveCancellation', 'Approve Cancellation') : t('Approve')}</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestDetailModal;
